/**
 * MotionAlerts — right-panel widget shown on the client Dashboard.
 * Polls /api/motion-alerts every 15s and shows a scrolling event log.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, User, Car, Volume2, ShieldAlert, ArrowRightLeft, Zap } from "lucide-react";

const TYPE_ICON = {
  motion: Zap,
  person: User,
  vehicle: Car,
  audio: Volume2,
  tamper: ShieldAlert,
  line_cross: ArrowRightLeft,
};

const SEV_COLOR = {
  low: "text-zinc-400 border-zinc-800",
  medium: "text-amber-400 border-amber-500/40",
  high: "text-red-400 border-red-500/60",
};

function timeAgo(iso) {
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

export default function MotionAlerts({ fullscreen = false }) {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let mounted = true;
    const load = () =>
      api.get("/motion-alerts?limit=30").then((r) => mounted && setEvents(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const rootCls = fullscreen
    ? "flex flex-col h-full min-h-0 bg-black"
    : "border-t border-zinc-900 p-3 flex flex-col max-h-[240px] shrink-0";

  return (
    <div className={rootCls}>
      {!fullscreen && (
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
          <AlertTriangle size={11} className="text-amber-400" />
          Motion Alerts
          <span className="ml-auto mono text-[9px] text-zinc-600">{events.length}</span>
        </div>
      )}
      <div
        data-testid="motion-alerts-list"
        className={`flex-1 overflow-auto -mr-1 pr-1 space-y-1 ${fullscreen ? "p-3" : ""}`}
      >
        {events.length === 0 && (
          <div className="mono text-[10px] text-zinc-600 uppercase tracking-widest text-center py-4">
            No alerts yet
          </div>
        )}
        {events.map((ev) => {
          const Icon = TYPE_ICON[ev.type] || Zap;
          const sevCls = SEV_COLOR[ev.severity] || SEV_COLOR.low;
          return (
            <div
              key={ev.id}
              data-testid={`motion-alert-${ev.id}`}
              className={`border ${sevCls} bg-zinc-950 px-2 py-1.5 flex items-start gap-2 ${fullscreen ? "py-2.5" : ""}`}
            >
              <Icon size={fullscreen ? 14 : 11} className="mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className={`mono ${fullscreen ? "text-[12px]" : "text-[10px]"} truncate flex items-center gap-1.5`}>
                  <span className="text-cyan-300">CH{String(ev.camera_channel).padStart(2, "0")}</span>
                  <span className="text-zinc-200 truncate">{ev.camera_name}</span>
                </div>
                <div className={`mono ${fullscreen ? "text-[10px]" : "text-[9px]"} text-zinc-500 flex items-center justify-between`}>
                  <span>{ev.label}</span>
                  <span className="text-zinc-600">{timeAgo(ev.timestamp)}</span>
                </div>
              </div>
              {ev.severity === "high" && (
                <span className="mono text-[8px] uppercase tracking-widest text-red-400 rec-blink">HI</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
